import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useCart } from '../../Utils/CartContext'
import { useStore } from '../../Components/useStore'
import { useLanguage } from '../../Utils/LanguageContext'

function Header() {
  const { pathname } = useLocation()
  const isHome = pathname === '/'
  const [isScrolled, setIsScrolled] = React.useState(false)
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const { lang, setLang, busy } = useLanguage()

  React.useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close drawer on route change
  React.useEffect(() => {
    setMobileOpen(false)
  }, [pathname])

  // Lock body scroll while mobile menu is open
  React.useEffect(() => {
    if (!mobileOpen) return undefined
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [mobileOpen])

  const { itemCount } = useCart()
  const { state } = useStore()
  const user = state.UserInfo
  const cartBadge = itemCount > 99 ? '99+' : String(itemCount).padStart(2, '0')
  const cartBg = isHome ? 'n900-bg' : 'bg2'
  const cartIconClr = isHome ? 'n0-clr' : 'n900-clr'
  const cartBadgeBg = isHome ? 'bg2' : 'n0-bg'
  const lightLogo = '/assets/images/logo/slimfast-whiteheader-logo.png?v=5'
  const darkLogo = '/assets/images/logo/slimfast-blackheader-logo.png?v=1'
  const barLogo = isHome ? lightLogo : darkLogo
  const drawerLogo = lightLogo
  const logoStyle = {
    maxHeight: isHome ? '58px' : '52px',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    display: 'block',
  }
  const langBtnBase = {
    minWidth: 40,
    height: 40,
    border: '1px solid',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.04em',
    lineHeight: 1,
    opacity: busy ? 0.7 : 1,
    cursor: busy ? 'wait' : 'pointer',
  }
  const langBtnBar = isHome
    ? { background: '#fff', color: '#111', borderColor: 'rgba(0,0,0,0.35)' }
    : { background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.45)' }
  const langBtnDrawer = {
    background: 'transparent',
    color: '#111',
    borderColor: 'rgba(0,0,0,0.3)',
  }

  const closeMobile = () => setMobileOpen(false)
  const toggleMobile = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setMobileOpen((v) => !v)
  }

  const LangToggle = ({ buttonStyle }) => {
    const nextLang = lang === 'EN' ? 'AR' : 'EN'
    const title = nextLang === 'AR' ? 'العربية' : 'English'
    return (
      <button
        type="button"
        className="header-lang-switch d-center radius-circle no-translate"
        data-no-translate
        style={{ ...langBtnBase, ...buttonStyle }}
        onClick={() => setLang(nextLang)}
        disabled={busy}
        title={title}
        aria-label={`Switch to ${nextLang === 'AR' ? 'Arabic' : 'English'}`}
      >
        {nextLang}
      </button>
    )
  }

  return (
    <>
      <header
        className={`header-section header-version8 ${isHome ? 'header-home-light' : 'n900-bg'} ${isScrolled ? 'header-fixed' : ''} ${mobileOpen ? 'mobile-nav-open' : ''}`}
      >
        <div className="container regular-contaienr">
          <div className="main-navbar py-1">
            <nav className="navbar-custom">
              <div className="d-flex align-items-center justify-content-between w-100 header-mobile-bar">
                <Link to="/" className="nav-brand d-lg-none d-block">
                  <img
                    src={barLogo}
                    alt="Slimfast"
                    style={{
                      ...logoStyle,
                      maxHeight: isHome ? '44px' : '42px',
                    }}
                  />
                </Link>
                <div className="d-flex align-items-center gap-2 d-lg-none">
                  <LangToggle buttonStyle={langBtnBar} />
                  <Link
                    to="/cart"
                    className={`shop-icon d-center radius-circle ${cartBg} position-relative`}
                    style={{ width: 44, height: 44 }}
                    aria-label="Cart"
                  >
                    <span className={`cart-count d-center ${cartBadgeBg} n900-clr fs-seven radius-circle`}>
                      {cartBadge}
                    </span>
                    <i className={`ph-bold ph-shopping-cart ${cartIconClr} fs-five`} />
                  </Link>
                  <button
                    className={`navbar-toggle-btn header-hamburger d-block d-lg-none ${mobileOpen ? 'open' : ''}`}
                    type="button"
                    aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                    aria-expanded={mobileOpen}
                    onClick={toggleMobile}
                  >
                    <span />
                    <span />
                    <span />
                    <span />
                  </button>
                </div>
              </div>

              <div className={`navbar-toggle-item ${mobileOpen ? 'is-open' : ''}`}>
                <div className="d-flex w-100 flex-column flex-lg-row align-items-start align-items-lg-center justify-content-between">
                  <div className="mobile-logo">
                    <Link
                      to="/"
                      className="nav-brand d-inline-flex align-items-center"
                      onClick={closeMobile}
                    >
                      <img
                        src={drawerLogo}
                        alt="Slimfast"
                        className="d-lg-none"
                        style={{ ...logoStyle, maxHeight: '48px' }}
                      />
                      <img
                        src={barLogo}
                        alt="Slimfast"
                        className="d-none d-lg-block"
                        style={logoStyle}
                      />
                    </Link>
                    <button
                      type="button"
                      className="navbar-toggle-btn header-hamburger d-lg-none d-block open"
                      aria-label="Close menu"
                      onClick={toggleMobile}
                    >
                      <span />
                      <span />
                      <span />
                      <span />
                    </button>
                  </div>

                  <ul className="custom-nav d-lg-flex d-grid gap-xxl-8 gap-xl-6 gap-lg-4 gap-4">
                    <li className="menu-item position-relative">
                      <Link to="/" className="position-relative" onClick={closeMobile}>
                        Home
                      </Link>
                    </li>
                    <li className="menu-item position-relative">
                      <Link to="/ingredients" className="position-relative" onClick={closeMobile}>
                        Ingredients
                      </Link>
                    </li>
                    <li className="menu-item position-relative">
                      <Link to="/about" className="position-relative" onClick={closeMobile}>
                        About Slimfast
                      </Link>
                    </li>
                    <li className="menu-item position-relative">
                      <Link to="/shop" className="position-relative" onClick={closeMobile}>
                        Shop
                      </Link>
                    </li>
                  
                    <li className="menu-item position-relative">
                      <Link to="/contact" onClick={closeMobile}>
                        Contact
                      </Link>
                    </li>
                  </ul>

                  <div className="d-flex align-items-center m-lg-0 m-auto justify-content-lg-start justify-content-center gap-xxl-8 gap-4 flex-wrap header-actions">
                    <div className="d-none d-lg-flex">
                      <LangToggle buttonStyle={langBtnBar} />
                    </div>
                    <div className="d-lg-none">
                      <LangToggle buttonStyle={langBtnDrawer} />
                    </div>
                    <Link
                      to="/cart"
                      className={`shop-icon d-none d-lg-flex d-center radius-circle ${cartBg} position-relative`}
                      onClick={closeMobile}
                    >
                      <span className={`cart-count d-center ${cartBadgeBg} n900-clr fs-seven radius-circle`}>
                        {cartBadge}
                      </span>
                      <i className={`ph-bold ph-shopping-cart ${cartIconClr} fs-five`} />
                    </Link>
                    <Link
                      to={user?.token ? '/dashboard' : '/sign-in'}
                      onClick={closeMobile}
                      className={
                        isHome
                          ? 'cmn-btn n900-bg py-xxl-4 py-3 px-xl-8 px-lg-7 px-6 radius100'
                          : 'cmn-btn bg2 alt py-xxl-4 py-3 px-xl-8 px-lg-7 px-6 radius100'
                      }
                    >
                      <span className={isHome ? 'n0-clr' : 'n900-clr'}>
                        {user?.token ? 'Dashboard' : 'Sign In'}
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            </nav>
          </div>
        </div>
      </header>
    </>
  )
}

export default Header
